import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { userManager, User } from '../../utils/auth';
import { useAuth } from '../../contexts/AuthContext';
import { completeNewPasswordChallenge } from '../../utils/cognito';
import { usePageTitle } from '../../hooks/usePageTitle';
import LandingNav from '../LandingNav';
import '../../styles/auth.css';
import '../../styles/landing.css';

export default function Login() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [session, setSession] = useState('');

  const fetchUserProfile = async (idToken: string): Promise<User | null> => {
    try {
      const response = await fetch(`${authConfig.apiUrl}/api/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Profile fetch error:', response.status);
        return null;
      }

      const data = await response.json();
      return data.user ?? null;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${authConfig.apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Check if user needs to change password (invited user first login)
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        setSession(data.session || '');
        setLoading(false);
        return;
      }

      if (data.authenticationResult) {
        const tokens = {
          accessToken: data.authenticationResult.AccessToken,
          idToken: data.authenticationResult.IdToken,
          refreshToken: data.authenticationResult.RefreshToken,
          expiresIn: data.authenticationResult.ExpiresIn,
          expiresAt: Date.now() + (data.authenticationResult.ExpiresIn * 1000),
        };

        const user = await fetchUserProfile(tokens.idToken);
        login(tokens, user || {} as User);
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const authResult = await completeNewPasswordChallenge({
        session,
        username: email,
        newPassword,
      });

      if (authResult) {
        const tokens = {
          accessToken: authResult.AccessToken!,
          idToken: authResult.IdToken!,
          refreshToken: authResult.RefreshToken!,
          expiresIn: authResult.ExpiresIn!,
          expiresAt: Date.now() + (authResult.ExpiresIn! * 1000),
        };

        const user = await fetchUserProfile(tokens.idToken);
        login(tokens, user || {} as User);
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  // Show password change form if needed
  if (needsPasswordChange) {
    return (
      <div className="landing-page">
        <LandingNav />

        {/* Auth Content */}
        <div className="auth-container">
          <div className="auth-header">
            <p className="auth-header-subtitle">Set Your New Password</p>
          </div>

          <div className="auth-card">
            <h2 className="auth-card-title">Welcome!</h2>
            <p className="text-center info-text" style={{ marginBottom: '24px' }}>
              Please set a new password to continue.
            </p>

            <form onSubmit={handlePasswordChange} className="auth-form">
              <div className="input-container">
                <span className="input-icon">
                  <Lock size={18} />
                </span>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="toggle-password-btn"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="input-container">
                <span className="input-icon">
                  <Lock size={18} />
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="toggle-password-btn"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="hint-text">
                Password must be at least 8 characters with uppercase, lowercase, and numbers
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" disabled={loading} className="auth-button">
                {loading ? 'Setting Password...' : 'Set New Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Normal login form
  return (
    <div className="landing-page">
      <LandingNav />

      {/* Auth Content */}
      <div className="auth-container">
        <div className="auth-header">
          <p className="auth-header-subtitle">
            Collaborate, upload, and showcase your creativity together!
          </p>
        </div>

        <div className="auth-card">
          <h2 className="auth-card-title">Login</h2>

          <form onSubmit={handleLogin} className="auth-form">
            <div className="input-container">
              <span className="input-icon">
                <Mail size={18} />
              </span>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div className="input-container">
              <span className="input-icon">
                <Lock size={18} />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="toggle-password-btn"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' }}>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="auth-link"
              >
                Forgot Password?
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} className="auth-button">
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
